import type { MovementPattern } from "./schemas/muscle.js";
import type { Workout, WorkoutSet } from "./schemas/workout.js";
import type { LogSetKind } from "./schemas/logSet.js";

// PR DETECTION. Pure functions — same module is re-used by:
//   - the web Progress tab (Records section + "Update MAX" pill)
//   - the SL→JL importer (seed each exercise's MAX from log history)
//   - any future server-side PR re-aggregation
//
// Decisions encoded here (per Q1 + Track 4, next/archive/FAMILY_APP_PARITY.md 2026-05-06):
//
//   * Two distinct concepts, two different bounds:
//       - E1RM_REP_CAP = 12. Epley's estimated-1RM formula is reliable
//         in 1-10 reps, OK 10-12, unreliable past 12. Used ONLY by
//         computeE1rmKg + the Progress chart helpers (progress.ts +
//         mobile useEntryHistory). 13-20 rep weighted sets don't
//         contribute inflated e1RM points.
//       - Rep-PR detection (heaviest weight at exactly N reps) is
//         UNCAPPED on the upper end — bounded only by the zod schema's
//         Reps.max(999). A 73-rep captain-leg-raise fires the trophy
//         pill and shows up in /api/me/records as a real PR.
//     Initially both were one constant REP_MAX_CAP=12 (0.78.0), raised
//     to 20 in 0.83.0 to surface high-rep accessory PRs. That was a
//     wrong fix — it inflated e1RM as a side-effect. 1.91.0 decouples.
//
//   * REP_TRUST_CAP_BY_PATTERN clamps which sets feed the MAX
//     auto-suggester. A 12-rep squat is informational, not a max signal —
//     the algorithm ignores it. Per-pattern because heavy compound lifts
//     have stable e1RM curves to ~6 reps; isolation/accessory work
//     diverges fast above ~10 reps.
//
//   * `fail=true` sets ARE valid evidence, for both PR detection and MAX
//     suggestions (AP-0p model, 2026-07-06). `reps` already holds the
//     clean reps completed — the failed rep was decremented at log time —
//     so `fail` is a pure label, and a 3×165 that ended in a failed 4th
//     is a genuine result. Nothing in this file filters on it.
//
//   * `weighted` set type only for v1. Bodyweight PR semantics (extra
//     weight on dips/pull-ups, rep PR on pure-bw) deferred until the
//     bw-factor table is wired into effective-load math.
//
// The MAX auto-derivation RAISE half moved to maxSuggest.ts (SP-PR), and
// the two rules that governed only it — the conservative suggest bias and
// the no-auto-seed-on-first-exercise decision — moved with it, so that a
// reader of that file sees the why without opening this one. What stays
// here is the shared base of the MAX/PR domain that neither half owns:
// E1RM_REP_CAP, epley, roundToHalfKg, the PR-eligibility predicate — and
// REP_TRUST_CAP_BY_PATTERN, whose only reader today is maxSuggest.ts. It
// stays for the same reason as roundToHalfKg (see below): pr.ts is the
// base both halves already import (maxSuggest.ts raises, maxRegression.ts
// lowers) and the dependency runs one way only — pr.ts imports neither —
// so a table either half may need belongs here rather than inside one of
// them.

// Epley e1RM accuracy ceiling. Literature: formula is reliable 1-10
// reps, OK 10-12, unreliable past 12. Used ONLY by computeE1rmKg +
// the Progress chart helpers in progress.ts / useEntryHistory.ts.
// Initially 12 (0.78.0); raised to 20 in 0.83.0 to surface
// high-rep accessory PRs — but that conflated two concepts. 1.91.0
// decouples: e1RM stays at 12 (this constant); rep-max PRs are
// bounded only by the zod schema's Reps.max(999).
export const E1RM_REP_CAP = 12;

// e1RM rep-range gate — the shared predicate every "is this set valid Epley
// evidence" site must use, so the bound can't diverge (it did once: a
// cap-only check let a 0-rep fail through as epley(w,0)=w). Below 1 there's
// no rep; above the cap Epley diverges. Weight positivity is a separate,
// per-caller check (some read `extra`, some `weight`).
export const isE1rmRepCount = (reps: number): boolean =>
  reps >= 1 && reps <= E1RM_REP_CAP;

// Per-pattern reps trust ceiling. Above this, sets are informational
// only — they don't trigger MAX suggestions. PR detection does NOT read
// this table: it is uncapped on purpose (see isPREligibleSet), so a
// high-rep set still records its rep-max bucket up to the zod schema's
// Reps.max(999); only the suggestion fails to fire.
export const REP_TRUST_CAP_BY_PATTERN: Record<MovementPattern, number> = {
  squat: 6,
  hinge: 6,
  "horizontal-push": 6,
  "vertical-push": 8,
  "horizontal-pull": 8,
  "vertical-pull": 8,
  lunge: 10,
  carry: 10,
  core: 10,
  isolation: 10,
  // Come `other`, cioe' come si comportava una riga di mobilita' PRIMA che la
  // categoria esistesse: il cambiamento non deve muovere nessuna suggestione.
  mobility: 10,
  cardio: 10,
  other: 10,
};

// The MAX family's presentation rounding: every proposed MAX is a plate
// value, so it lands on 0.5 kg. It lives here because both halves of the
// derivation read it (maxSuggest.ts raises, maxRegression.ts lowers) and
// neither owns it: the two mirrors do not import each other, so pr.ts —
// the base they already both read — is the only place either can reach.
export const roundToHalfKg = (x: number): number => Math.round(x * 2) / 2;

// Epley estimated 1RM. weight × (1 + reps/30). Returns kg.
// At reps=1 returns weight × 31/30 (≈ +3.3%), NOT the weight unchanged:
// the formula has no rep count at which it is the identity. At reps>30
// the formula explodes —
// callers should always gate by E1RM_REP_CAP (12) first.
export const epley = (weightKg: number, reps: number): number =>
  weightKg * (1 + reps / 30);

// Epley INVERTED. `epley` answers "what single is this set worth"; this answers
// the question a prescription on the EFFORT axis asks — "you want me at RIR 2
// for 8 reps, so what do I put on the bar?" (PG-9, measurement-contract.md
// §17). Same formula, so it lives beside its twin: written anywhere else the
// two would drift apart the first time the exponent is touched.
//
// ⛔ Same validity band, deliberately. Past E1RM_REP_CAP Epley diverges and
// `computeE1rmKg` returns null rather than stamp an inflated number; the
// inverse refuses for the identical reason, because a load derived out there
// is a number nobody should put on a bar. Antenore 2026-09-01, choosing this
// over widening the band with a confidence label: the house says it does not
// know. A non-positive e1RM is a MISSING one, not a light one.
export const weightForE1rm = (
  e1rmKg: number,
  reps: number,
): number | null =>
  Number.isFinite(e1rmKg) && e1rmKg > 0 && isE1rmRepCount(reps)
    ? e1rmKg / (1 + reps / 30)
    : null;

// #534: the set-level tag that marks a set done with compromised technique.
// It lives in the existing user `flag` namespace (schemas/tag.ts), so the
// LogSet schema does not change and metria_set_tag already writes it. Read
// by computeE1rmKg (no e1RM) and by the records read (no rep-max record);
// tonnage and hard sets keep the set.
export const FORM_BREAK_TAG = "flag:form-break";

export const isFormBreakSet = (tags: readonly string[] | undefined): boolean =>
  tags?.includes(FORM_BREAK_TAG) === true;

// D9 (Antenore, 01/10): a set's e1RM at the effort it was logged with. "N
// reps at RIR r" is N + r reps to failure, the reading the effort arm gives a
// target, so the anchor and the load it proposes use one rule. RIR null (no
// effort recorded): the reps alone. Null outside Epley's band: a set whose
// reps + RIR pass 12 is no anchor, so a lighter set wins the pick, or the
// caller falls back (the «today» draft: the last session's best set).
export const e1rmAtEffort = (
  weightKg: number,
  reps: number,
  rir: number | null,
): number | null => {
  const toFailure = reps + (rir ?? 0);
  if (!(weightKg > 0) || !isE1rmRepCount(reps) || !isE1rmRepCount(toFailure)) return null;
  return epley(weightKg, toFailure);
};

// e1RM eligibility + computation for a single LogSet payload. Returns the
// e1RM in kg when the set qualifies, null otherwise. Requires weighted
// kind, 1 ≤ reps ≤ E1RM_REP_CAP (12), weight > 0. A failed set is NOT
// excluded (AP-0p model, 2026-07-06): `reps` already holds the clean reps
// completed (the failed rep was decremented at log time), and `fail` is a
// pure label — a genuine 3×165 that ended in a failed 4th is real 1RM
// evidence. Past 12 reps Epley diverges so we return null rather than stamp
// an inflated number. Accepts the flat LogSet-style shape so the API route
// can stamp e1rmKg at POST time without converting to the legacy nested
// WorkoutSet shape first. Reused by computeSessionResult at session-
// aggregate time so eligibility lives in exactly one place.
//
// #534: a set tagged FORM_BREAK_TAG is no e1RM evidence either. The load
// was moved, so it stays in tonnage and hard sets (setMetrics does not read
// this), but a rep done with broken technique proves no ceiling: no e1RM, so
// no top set and no e1RM point. `tags` is optional so a caller holding a
// shape without set tags (the nested WorkoutSet) reads as untagged.
export const computeE1rmKg = (set: {
  kind: LogSetKind;
  reps?: number;
  weight?: number;
  tags?: readonly string[];
}): number | null => {
  if (isFormBreakSet(set.tags)) return null;
  if (set.kind !== "weighted") return null;
  if (set.reps === undefined || set.weight === undefined) return null;
  if (!isE1rmRepCount(set.reps)) return null;
  if (set.weight <= 0) return null;
  return epley(set.weight, set.reps);
};

// Per-rep-max PR record. The actual `reps` may exceed the bucket
// (e.g., 100 kg × 5 occupies the 1RM, 2RM, 3RM, 4RM, 5RM buckets, all
// recording weight=100, reps=5).
export type PRRecord = {
  weightKg: number;
  reps: number;
  setAt: string; // ISO date of the workout that set the PR
  workoutId: string;
};

// Outer key: exerciseId (slug). Inner key: rep-max bucket (1..N where N
// is whatever reps the heaviest qualifying set actually performed —
// bounded by the zod schema's Reps.max(999), not by E1RM_REP_CAP).
// Use Record over Map so it JSON-serialises cleanly.
export type PRsByExercise = Record<string, Record<number, PRRecord>>;

// Year keyed by 4-digit calendar year. Same shape as PRsByExercise per
// year, plus the conventional "all" key for the all-time view.
export type YearlyPRs = Record<string, PRsByExercise>;

// Filters to PR-eligible sets. Accepts weighted + bodyweight (1.99.5);
// rejects p1rm (no concrete weight to record), loaded-carry, cardio.
// A FAILED set IS eligible (AP-0p model, 2026-07-06): its `reps` are the
// clean reps completed and `fail` is a pure label, so a 3×165 that ended
// in a failed 4th is a genuine PR — Antenore's example. For weighted sets
// the `weight` must be positive; for bodyweight sets `extra` may be 0
// (pure-bw progression surfaces PRs via empty higher-r buckets).
//
// Upper bound on `reps` lives in the zod schema (Reps.max(999)) — not
// here. PR detection is uncapped on purpose: a 73-rep captain-leg-raise
// is a legitimate rep-PR even though Epley wouldn't apply at that
// volume. e1RM gating is a separate concern (E1RM_REP_CAP, 12).
//
// Type guard narrows to the two kinds that share a `reps: number`
// field, so `set.reps` access stays type-safe in the callers.
export const isPREligibleSet = (
  s: WorkoutSet,
): s is Extract<WorkoutSet, { type: "weighted" | "bodyweight" }> => {
  if (s.type !== "weighted" && s.type !== "bodyweight") return false;
  if (s.reps < 1) return false;
  if (s.type === "weighted" && s.weight <= 0) return false;
  return true;
};

// Effective "weight" dimension for PR bucket comparison. Weighted sets
// use the lifted load directly; bodyweight sets use the optional
// `extra` (0 when undefined = pure bodyweight). Encapsulates the
// branch so prsFromHistory + isSetPR don't repeat the per-type cast.
// Per-exercise scope means a single PRsByExercise bucket only mixes
// one kind in practice — catalog exercises are either weighted or
// bodyweight, not both.
export const prWeightOf = (s: WorkoutSet): number => {
  if (s.type === "weighted") return s.weight;
  if (s.type === "bodyweight") return s.extra ?? 0;
  return 0;
};

// A recorded PR weight of 0 can only come from a pure-bodyweight rep PR:
// isPREligibleSet requires weight > 0 on weighted sets, and bodyweight
// sets record `extra ?? 0`. Display surfaces branch on this instead of
// printing "0 kg" (which reads as a data bug) — one predicate so the
// invariant lives next to the eligibility rule that guarantees it.
export const isPureBodyweightPr = (weightKg: number): boolean =>
  weightKg <= 0;

// Walk every workout in chronological order, fold each weighted set into
// its rep-max buckets. A set of weight×reps occupies buckets 1..reps,
// each recording the weight (heavier wins). Per-exercise scope. Bucket
// count is uncapped on the upper end — bounded only by the zod schema's
// Reps.max(999).
export const prsFromHistory = (workouts: readonly Workout[]): PRsByExercise => {
  const out: PRsByExercise = {};
  // Sort oldest first so ties go to the earliest occurrence (matches user
  // intuition: "I first hit 100×5 on 2024-03-01" — even if they hit it
  // again later, the recorded date is the original PR.)
  const sorted = [...workouts].sort(
    (a, b) =>
      new Date(a.startedAt).getTime() - new Date(b.startedAt).getTime(),
  );
  for (const w of sorted) {
    for (const entry of w.entries) {
      for (const set of entry.sets) {
        if (!isPREligibleSet(set)) continue;
        const exId = entry.exerciseId;
        const bucket = out[exId] ?? (out[exId] = {});
        const upTo = set.reps;
        const setWeight = prWeightOf(set);
        for (let r = 1; r <= upTo; r++) {
          const current = bucket[r];
          // Strictly heavier wins. Equal-weight ties keep the earliest.
          // For bodyweight sets `setWeight` is `extra ?? 0`, so empty
          // higher-r buckets surface "more reps than ever" PRs even
          // when the load stays at 0.
          if (!current || setWeight > current.weightKg) {
            bucket[r] = {
              weightKg: setWeight,
              reps: set.reps,
              setAt: w.startedAt,
              workoutId: w.id,
            };
          }
        }
      }
    }
  }
  return out;
};

// Yearly slice: best per rep-max within each calendar year (UTC year of
// the workout's startedAt). The "all" key holds the all-time aggregate
// — same as prsFromHistory(workouts).
export const yearlyPRsFromHistory = (
  workouts: readonly Workout[],
): YearlyPRs => {
  const byYear: Record<string, Workout[]> = {};
  for (const w of workouts) {
    const year = String(new Date(w.startedAt).getUTCFullYear());
    (byYear[year] ?? (byYear[year] = [])).push(w);
  }
  const out: YearlyPRs = { all: prsFromHistory(workouts) };
  for (const [year, list] of Object.entries(byYear)) {
    out[year] = prsFromHistory(list);
  }
  return out;
};

// Per-set PR check used at click time when the athlete marks a set
// done. Returns true when the just-completed set strictly beats the
// existing record for at least one rep-max bucket the set occupies.
// History is the prior workouts (the in-progress draft must be excluded
// upstream, otherwise the set is compared against itself).
//
// Same eligibility rules as `prsFromHistory` — weighted OR bodyweight
// (1.99.5), reps >= 1 (upper bound is the zod schema only). A failed set
// is NOT excluded: see isPREligibleSet, AP-0p model.
// Bodyweight uses `extra ?? 0` as the comparison weight; pure-bw
// progressions surface PRs through empty higher-r buckets when the rep
// count exceeds prior history.
export const isSetPR = (params: {
  set: WorkoutSet;
  exerciseId: string;
  history: PRsByExercise;
}): boolean => {
  if (!isPREligibleSet(params.set)) return false;
  const buckets = params.history[params.exerciseId];
  if (!buckets) return true; // first-ever logged set on this lift
  const setWeight = prWeightOf(params.set);
  const upTo = params.set.reps;
  for (let r = 1; r <= upTo; r++) {
    const current = buckets[r];
    if (!current || setWeight > current.weightKg) return true;
  }
  return false;
};

// Session-level volume PR check for a single exercise within a session.
// Returns true when the current session's total volume for the exercise
// strictly beats every prior session's volume for that exercise at a
// comparable set count (± 1). Caller pre-filters history to the same
// exercise; this function does not look up exerciseSlug — it just
// compares numbers.
//
// Why set-count gating: volume PRs only make sense at comparable work
// scale. A 5-set hypertrophy day beating a 3-set strength day on raw
// kg is not a meaningful PR signal. The ± 1 tolerance handles minor
// session-to-session variability (4 vs 5 sets is still comparable; 3
// vs 5 is not).
//
// Returns false on empty history (first-ever session): volume PRs are
// "beat your previous best", and there is no previous best to beat.
// Set-level isSetPR has different first-ever semantics (true on first
// weighted lift) because rep-max PRs naturally bootstrap from zero.
//
// Used by computeSessionResult to stamp ExerciseResult.isPrVolume at
// session-save time.
export const isVolumeSessionPR = (params: {
  currentVolumeKg: number;
  currentSetCount: number;
  history: ReadonlyArray<{ volumeKg: number; setCount: number }>;
}): boolean => {
  if (params.currentVolumeKg <= 0) return false;
  if (params.currentSetCount < 1) return false;
  if (params.history.length === 0) return false;
  for (const prior of params.history) {
    if (Math.abs(prior.setCount - params.currentSetCount) > 1) continue;
    if (prior.volumeKg >= params.currentVolumeKg) return false;
  }
  // Survived the gauntlet: if any prior at comparable set count had
  // ≥ current volume we'd have returned false above; if we got here
  // either every comparable prior was strictly smaller (PR), or there
  // were no comparable priors (no PR — see below).
  for (const prior of params.history) {
    if (Math.abs(prior.setCount - params.currentSetCount) <= 1) {
      return true;
    }
  }
  // No comparable prior at all → can't claim a volume PR. Same logic
  // as "first-ever returns false": you can't beat what doesn't exist.
  return false;
};
